import {AgendaelementId, EnkelGruppeModell, GruppeId, KopierSakTilMøterValideringsfeil, KopierTilMøterSpesifikasjon, MøteModellEnkel, MøteOgNySakModell, MøteRessursClient, OpprettAgendaelementValideringsfeil, SakRessursClient, TynnMøteModell, ValideringsresultatModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {ChangeDetectionStrategy, Component, Inject, Injectable, Signal, signal} from "@angular/core";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatInputModule} from "@angular/material/input";
import {MatFormFieldModule} from "@angular/material/form-field";
import {TranslateModule} from "@ngx-translate/core";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MøteTilgjengelighetForVelgMøterKomponent, MøteTilgjengelighetForVelgMøterKomponent_DI_TOKEN, VelgMøterKomponent} from "web-ng2/moduler/møte/komponenter/velg-møter/velg-møter";
import {lesMøtedato2} from "felles-ng2/modell/møteModellHjelpere";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {tomAgendaelementModell, tomGruppeModell} from "felles-ng2/modell/tommeModeller";

/** Sann dersom bruker kopierte saker, false dersom bruker avbrøt */
export type KopierSakDialogRespons = boolean;

const debug = initDebugger('KopierMøteDialogKomponent');


@Component({
    selector: 'kopier-sak-dialog',
    templateUrl: 'kopier-sak-dialog.html',
    styleUrls: ['kopier-sak-dialog.scss'],
    providers: [{provide: MøteTilgjengelighetForVelgMøterKomponent_DI_TOKEN, useFactory: (komponent: KopierSakDialogKomponent) => komponent.møteTilgjengelighetForVelgMøterKomponent(), deps: [KopierSakDialogKomponent]}],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        ReactiveFormsModule,
        MatInputModule,
        MatFormFieldModule,
        TranslateModule,
        TipsKomponent,
        InlineMeldingKomponent,
        VelgMøterKomponent,
        SubmitButtonKomponent,
        FormaterZonedDateTimePipe,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KopierSakDialogKomponent {

    static vis(dialog: MatDialog, sak: Promise<AgendaelementModellDLM>): MatDialogRef<KopierSakDialogKomponent, KopierSakDialogRespons> {
        return dialog.open(KopierSakDialogKomponent, {
            data: {sak} as DialogParametre,
            disableClose: true,
            ariaLabelledBy: 'kopier-sak-dialog-tittel',
            minHeight: 400,
            width: '600px'
        });
    }

    skjema = new FormGroup({
        moeter: new FormControl<MøteModellEnkel[]>([], {nonNullable: true, validators: Validators.required}),
    });

    sak = signal<AgendaelementModellDLM>(tomAgendaelementModell());
    kopiertTilMoeter = signal<KopiertTilMøte[]>([]);
    gruppe = signal<EnkelGruppeModell>(tomGruppeModell());
    modus = signal<'skjema' | 'kvittering'>('skjema');
    harFeil = signal(false);
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());
    /** liste med valideringsfeil, trenger ikke å være et signal da info ikke vises før harFeil()==true */
    feiLPerMoete: ValideringsfeilOgMøteinfo[] = [];

    private senesteLåsteMøtePerGruppe = new Map<GruppeId, Promise<MøteModellEnkel>>();

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: KopierSakDialogKomponentAvhengigheter,
                public dialogRef: MatDialogRef<KopierSakDialogKomponent, KopierSakDialogRespons>,
                private navigasjon: Navigasjon,
                private oversettelser: Oversettelser,
                private kontekst: BrukerkontekstTilbyder) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.initSak(parametre.sak);
    }

    gaaTilSak(sak: AgendaelementModellDLM) {
        this.navigasjon.gåTilMøteOgSak(sak.moete.id, sak.id);
    }

    async kopier() {
        await this.lagrer.utfør(async () => {
            const resultat = await this.avhengigheter.valider(this.sak().id, this.somSpesifikasjon());
            this.setValideringsfeil(resultat);
            if (this.harFeil()) return;

            const opprettedeSaker = await this.avhengigheter.kopier(this.sak().id, this.somSpesifikasjon());
            this.kopiertTilMoeter.set(opprettedeSaker.map(sak => ({moete: sak.moete, sak: sak.nySak, gruppe: this.finnGruppe(sak.nySak.eiendeGruppeId)})));
            this.modus.set('kvittering');
        });
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    møteTilgjengelighetForVelgMøterKomponent(): MøteTilgjengelighetForVelgMøterKomponent {
        return new MøteTilgjengelighetForVelgMøterKomponentForKopierMøte(this.oversettelser, this.sak, gruppeId => this.avhengigheter.finnSenesteLåsteMøte(gruppeId));
    }

    // MøteTilgjengelighetForVelgMøterKomponent implementasjon

    async erMøteTilgjengeligForValg(møte: MøteModellEnkel): Promise<boolean> {
        if (!this.senesteLåsteMøtePerGruppe.has(møte.eiendeGruppeId))
            this.senesteLåsteMøtePerGruppe.set(møte.eiendeGruppeId, this.avhengigheter.finnSenesteLåsteMøte(møte.eiendeGruppeId))

        const senesteLåsteMøte = await this.senesteLåsteMøtePerGruppe.get(møte.eiendeGruppeId);
        if (erNil(senesteLåsteMøte)) return true; // ingen låste møter i gruppen
        return lesMøtedato2(senesteLåsteMøte).erTidligereEnn(lesMøtedato2(møte));
    }

    årsak(møte: MøteModellEnkel): string {
        return this.oversettelser.oversett(i18n('Sak kan ikke kopieres til låst møte'));
    }

    // private hjelpemetoder

    private async initSak(sakpromise: Promise<AgendaelementModellDLM>) {
        this.sak.set(await this.laster.utfør(() => sakpromise));
        this.gruppe.set(this.finnGruppe(this.sak().eiendeGruppeId));
    }

    private finnGruppe(gruppeId: GruppeId): EnkelGruppeModell {
        return this.kontekst.autentisertBruker().finnAutentiserteGruppe(gruppeId)!;
    }

    private somSpesifikasjon(): KopierTilMøterSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            moeter: verdier.moeter!.map(møte => møte.id)
        }
    }

    private setValideringsfeil(resultat: KopierSakTilMøterValideringsfeil) {
        this.feiLPerMoete = Object.entries(resultat.feil)
          .map(([møteId, feil]) => {
              const møte = this.skjema.value.moeter!.find(møte => møte.id == møteId)!;
              return {
                  moete: møte,
                  gruppe: this.finnGruppe(møte.eiendeGruppeId),
                  valideringsfeil: this.valideringsmelding(feil)!
              };
          })
          .filter(feil => erIkkeNil(feil.valideringsfeil));
        this.harFeil.set(erIkkeTomListe(this.feiLPerMoete));
    }

    private valideringsmelding(feilPåGruppe: ValideringsresultatModell<OpprettAgendaelementValideringsfeil>): string | null {
        if (feilPåGruppe.feil.some(feil => feil.feilkode === OpprettAgendaelementValideringsfeil.protokoll_låst))
            return this.oversettelser.oversett(i18n('Møtet er låst for redigering'));
        return null;
    }
}

/** {@link MøteTilgjengelighetForVelgMøterKomponent} implementasjon som signaliserer at låste møter ikke kan velges i nedtrekksmenyen til {@link VelgMøterKomponent} */
export class MøteTilgjengelighetForVelgMøterKomponentForKopierMøte implements MøteTilgjengelighetForVelgMøterKomponent {
    private senesteLåsteMøtePerGruppe = new Map<GruppeId, Promise<MøteModellEnkel>>();

    constructor(protected oversettelser: Oversettelser, protected sak: Signal<AgendaelementModellDLM>, protected finnSenesteLåsteMøte: (gruppeId: GruppeId) => Promise<MøteModellEnkel>) {
    }

    async erMøteTilgjengeligForValg(møte: MøteModellEnkel): Promise<boolean> {
        if (!this.senesteLåsteMøtePerGruppe.has(møte.eiendeGruppeId))
            this.senesteLåsteMøtePerGruppe.set(møte.eiendeGruppeId, this.finnSenesteLåsteMøte(møte.eiendeGruppeId))

        const senesteLåsteMøte = await this.senesteLåsteMøtePerGruppe.get(møte.eiendeGruppeId);
        if (erNil(senesteLåsteMøte)) return true; // ingen låste møter i gruppen
        return lesMøtedato2(senesteLåsteMøte).erTidligereEnn(lesMøtedato2(møte));
    }

    årsak(møte: MøteModellEnkel): string {
        return this.oversettelser.oversett(i18n('Sak kan ikke kopieres til låst møte'));
    }
}

export interface DialogParametre {
    sak: Promise<AgendaelementModellDLM>;
}

interface KopiertTilMøte {
    sak: AgendaelementModellDLM;
    moete: TynnMøteModell;
    gruppe: EnkelGruppeModell;
}

interface ValideringsfeilOgMøteinfo {
    moete: MøteModellEnkel;
    gruppe: EnkelGruppeModell;
    valideringsfeil: string;
}

@Injectable()
export class KopierSakDialogKomponentAvhengigheter {

    constructor(private møteRessurs: MøteRessursClient,
                private sakRessurs: SakRessursClient,
                private sakDatatjeneste: SakDatatjeneste) {
    }

    finnSenesteLåsteMøte(gruppeId: GruppeId): Promise<MøteModellEnkel> {
        return this.møteRessurs.finnSenesteLåsteMøte({gruppeId: gruppeId}).somPromise();
    }

    async valider(kildesak: AgendaelementId, spesifikasjon: KopierTilMøterSpesifikasjon): Promise<KopierSakTilMøterValideringsfeil> {
        return this.sakRessurs.validerKopierTilMøter(kildesak, spesifikasjon).somPromise()
    }

    async kopier(kildesakId: AgendaelementId, spesifikasjon: KopierTilMøterSpesifikasjon): Promise<MøteOgNySakModell[]> {
        const saker = await this.sakDatatjeneste.kopierTilMøter(kildesakId, spesifikasjon);
        return saker.data;
    }
}
